/**
 * Lightweight card-in-guide check: strong edges along all four sides of the
 * guide rectangle (typical when an ID card fills the frame).
 */

export type GuideCrop = { sx: number; sy: number; sw: number; sh: number };

const ANALYZE_W = 160;
const EDGE_BAND = 10;
const SIDE_COVERAGE = 0.38;
const GRAD_THRESHOLD = 24;

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function sideCoverage(
  gray: Float32Array,
  w: number,
  h: number,
  side: 'top' | 'bottom' | 'left' | 'right'
): number {
  let hits = 0;
  let samples = 0;

  if (side === 'top' || side === 'bottom') {
    const y0 = side === 'top' ? 1 : Math.max(1, h - EDGE_BAND);
    const y1 = side === 'top' ? Math.min(h - 1, EDGE_BAND) : h - 1;
    for (let x = 2; x < w - 2; x += 2) {
      samples += 1;
      let maxG = 0;
      for (let y = y0; y < y1; y++) {
        const a = gray[y * w + x];
        const b = gray[(y - 1) * w + x];
        maxG = Math.max(maxG, Math.abs(a - b));
      }
      if (maxG >= GRAD_THRESHOLD) hits += 1;
    }
  } else {
    const x0 = side === 'left' ? 1 : Math.max(1, w - EDGE_BAND);
    const x1 = side === 'left' ? Math.min(w - 1, EDGE_BAND) : w - 1;
    for (let y = 2; y < h - 2; y += 2) {
      samples += 1;
      let maxG = 0;
      for (let x = x0; x < x1; x++) {
        const a = gray[y * w + x];
        const b = gray[y * w + (x - 1)];
        maxG = Math.max(maxG, Math.abs(a - b));
      }
      if (maxG >= GRAD_THRESHOLD) hits += 1;
    }
  }

  return samples === 0 ? 0 : hits / samples;
}

function cornerScore(gray: Float32Array, w: number, h: number, corner: 'tl' | 'tr' | 'bl' | 'br'): boolean {
  const cw = Math.min(18, Math.floor(w / 4));
  const ch = Math.min(18, Math.floor(h / 4));
  let x0 = 0;
  let y0 = 0;
  if (corner === 'tr' || corner === 'br') x0 = w - cw;
  if (corner === 'bl' || corner === 'br') y0 = h - ch;

  let horiz = 0;
  let vert = 0;
  let n = 0;
  for (let y = y0 + 1; y < y0 + ch - 1; y++) {
    for (let x = x0 + 1; x < x0 + cw - 1; x++) {
      const gx = Math.abs(gray[y * w + x] - gray[y * w + (x - 1)]);
      const gy = Math.abs(gray[y * w + x] - gray[(y - 1) * w + x]);
      if (gx >= GRAD_THRESHOLD) horiz += 1;
      if (gy >= GRAD_THRESHOLD) vert += 1;
      n += 1;
    }
  }
  if (n === 0) return false;
  // L-corner: both directions present
  return horiz / n >= 0.08 && vert / n >= 0.08;
}

/**
 * Returns true when all four sides (and corners) show card-like edges inside the guide.
 */
export function isCardAlignedInGuide(
  video: HTMLVideoElement,
  crop: GuideCrop,
  scratch?: HTMLCanvasElement
): boolean {
  if (!crop.sw || !crop.sh || !video.videoWidth) return false;

  const canvas = scratch ?? document.createElement('canvas');
  const aspect = crop.sh / crop.sw;
  const aw = ANALYZE_W;
  const ah = Math.max(48, Math.round(ANALYZE_W * aspect));
  canvas.width = aw;
  canvas.height = ah;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  ctx.drawImage(video, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, aw, ah);
  let image: ImageData;
  try {
    image = ctx.getImageData(0, 0, aw, ah);
  } catch {
    return false;
  }

  const { data } = image;
  const gray = new Float32Array(aw * ah);
  for (let i = 0, p = 0; i < gray.length; i++, p += 4) {
    gray[i] = luminance(data[p], data[p + 1], data[p + 2]);
  }

  const sides = [
    sideCoverage(gray, aw, ah, 'top'),
    sideCoverage(gray, aw, ah, 'bottom'),
    sideCoverage(gray, aw, ah, 'left'),
    sideCoverage(gray, aw, ah, 'right'),
  ];
  const sidesOk = sides.every((c) => c >= SIDE_COVERAGE);

  const cornerHits = (
    [
      cornerScore(gray, aw, ah, 'tl'),
      cornerScore(gray, aw, ah, 'tr'),
      cornerScore(gray, aw, ah, 'bl'),
      cornerScore(gray, aw, ah, 'br'),
    ] as boolean[]
  ).filter(Boolean).length;

  // Need all four sides plus at least three corners (lighting can hide one).
  return sidesOk && cornerHits >= 3;
}
